import { useState, useEffect } from 'react';
import { tasksApi, jobsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/DataTable';
import { StatusBadge, PriorityBadge } from '@/components/StatusBadge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, CalendarIcon, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

const taskStatuses = ['pending', 'in_progress', 'completed', 'cancelled'];
const priorities = ['low', 'medium', 'high'];

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [cases, setCases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    case_id: '',
    due_date: null,
    priority: 'medium',
    status: 'pending',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tasksData, casesData] = await Promise.all([
        tasksApi.getAll(),
        jobsApi.getAll().catch(() => []),  // Use jobs API instead of cases
      ]);
      setTasks(tasksData);
      setCases(casesData);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', case_id: '', due_date: null, priority: 'medium', status: 'pending' });
    setEditingTask(null);
  };

  const openDialog = (task = null) => {
    if (task) {
      setEditingTask(task);
      setFormData({
        title: task.title || '',
        description: task.description || '',
        case_id: task.case_id || '',
        due_date: task.due_date ? new Date(task.due_date) : null,
        priority: task.priority || 'medium',
        status: task.status || 'pending',
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      due_date: formData.due_date ? formData.due_date.toISOString() : null,
    };
    try {
      if (editingTask) {
        await tasksApi.update(editingTask.id, submitData);
        toast.success('Task updated');
      } else {
        await tasksApi.create(submitData);
        toast.success('Task created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save task');
    }
  };

  const handleDelete = async (task) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await tasksApi.delete(task.id);
      toast.success('Task deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleComplete = async (task) => {
    try {
      await tasksApi.update(task.id, { ...task, status: 'completed' });
      toast.success('Task completed');
      fetchData();
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };

  const getCaseNumber = (caseId) => {
    const caseItem = cases.find(c => c.id === caseId);
    return caseItem?.job_number || caseItem?.case_number || '-';
  };

  const columns = [
    { key: 'title', label: 'Title', render: (val, row) => (
      <div>
        <span className="font-medium">{val}</span>
        {row.source === 'ppm' && (
          <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">PPM</span>
        )}
        {row.compliance && (
          <span className="ml-1 text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">COMPLIANCE</span>
        )}
      </div>
    )},
    { key: 'asset_name', label: 'Asset/Site', render: (val, row) => val || row.site_name || getCaseNumber(row.case_id) },
    { key: 'category', label: 'Category', render: (val) => val || '-' },
    { key: 'due_date', label: 'Due Date', render: (val) => val ? format(new Date(val), 'MMM d, yyyy') : '-' },
    { key: 'priority', label: 'Priority', render: (val) => <PriorityBadge priority={val} /> },
    { key: 'status', label: 'Status', render: (val) => <StatusBadge status={val} /> },
  ];

  const actions = [
    { label: 'Complete', icon: CheckCircle, onClick: handleComplete },
    { label: 'Edit', icon: Pencil, onClick: openDialog },
    { label: 'Delete', icon: Trash2, onClick: handleDelete, destructive: true },
  ];

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Tasks</h1>
          <p className="text-sm text-stone-500 mt-1">Manage tasks and reminders</p>
        </div>
        <Button onClick={() => openDialog()} className="bg-emerald-900 hover:bg-emerald-800" data-testid="add-task-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Task
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={tasks}
        actions={actions}
        isLoading={isLoading}
        emptyMessage="No tasks found. Create your first task!"
      />

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md" data-testid="task-dialog">
          <DialogHeader>
            <DialogTitle>{editingTask ? 'Edit Task' : 'Create New Task'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                data-testid="task-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="case">Linked Case</Label>
              <Select value={formData.case_id} onValueChange={(val) => setFormData({ ...formData, case_id: val })}>
                <SelectTrigger data-testid="task-case-select">
                  <SelectValue placeholder="Select case (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {cases.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.case_number} - {c.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="due_date">Due Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn('w-full justify-start text-left font-normal', !formData.due_date && 'text-muted-foreground')}
                      data-testid="task-due-date-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.due_date ? format(formData.due_date, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.due_date}
                      onSelect={(date) => setFormData({ ...formData, due_date: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select value={formData.priority} onValueChange={(val) => setFormData({ ...formData, priority: val })}>
                  <SelectTrigger data-testid="task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priorities.map((p) => (
                      <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(val) => setFormData({ ...formData, status: val })}>
                <SelectTrigger data-testid="task-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {taskStatuses.map((s) => (
                    <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                data-testid="task-description-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-900 hover:bg-emerald-800" data-testid="task-submit-btn">
                {editingTask ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
